import { useRef, useState, ReactNode, useEffect } from 'react'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@radix-ui/react-popover'
import { Command, CommandInput } from 'cmdk'
import { IconChevronDown, IconCircleX } from '@tabler/icons-react'

import { cn } from 'utils/classMerge'
import { translate } from 'utils/translation'

import Text from '../text'
import { getFontClass } from '../utils/get-font-class'
import Label from '../label'
import ComboBoxIcon from '../combobox/components/combobox-icon'
import { Tooltip } from '../tooltip'

export interface SelectPrimitiveProps {
  label?: string
  helper?: string
  placeholder?: string
  state: 'default' | 'disabled'
  size: 'default' | 'large'
  icon?: ReactNode
  value?: string
  onValueChange?: (value: string) => void
  onBlur?: () => void
  onFocus?: () => void
  className?: string
  boxClassName?: string
  hasError?: boolean
  children: ReactNode
  isFilter?: boolean
  onClickClearFilter?: () => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  tooltipContent?: ReactNode
}

export default function SelectPrimitive({
  label,
  helper,
  placeholder,
  state = 'default',
  size = 'default',
  icon,
  value,
  onValueChange,
  onBlur,
  onFocus,
  className,
  boxClassName = '',
  hasError = false,
  children,
  isFilter = false,
  onClickClearFilter,
  open,
  onOpenChange,
  tooltipContent,
}: SelectPrimitiveProps) {
  const fontClass = getFontClass(className)
  const [isFocused, setIsFocused] = useState(false)
  const [showOptions, setShowOptions] = useState(false)
  const [isMouseOnInput, setIsMouseOnInput] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFocus = () => {
    setIsFocused(!isFocused)
    inputRef.current?.focus()
    onFocus?.()
  }

  useEffect(() => {
    if (state === 'disabled' && isFocused && isFilter) {
      setIsFocused(false)
    }
  }, [state, isFocused, isFilter])

  const renderCommandInput = () => (
    <div
      className={cn('tw-relative tw-w-full', {
        'tw-cursor-pointer': isFilter,
        'tw-cursor-default': !isFilter,
      })}
    >
      <CommandInput
        className={cn(
          'tw-w-full tw-rounded-md tw-border tw-border-solid tw-py-2 tw-pr-3 tw-shadow-sm',
          {
            'tw-pl-10': icon,
            'tw-pl-3': !icon,
            'tw-h-9': size === 'default',
            'tw-h-10': size !== 'default',
            'tw-cursor-not-allowed tw-border-gray-300 tw-bg-gray-100':
              state === 'disabled',
            'tw-border-2 tw-border-orange-500': isFocused,
            'tw-border-zinc-300': !isFocused && !hasError,
            'tw-border-rose-600': hasError,
            'tw-font-medium tw-caret-transparent placeholder:tw-text-sm placeholder:tw-text-zinc-900':
              isFilter,
            'tw-text-transparent': isFilter,
          },
          fontClass,
          className,
        )}
        onClick={handleFocus}
        onValueChange={onValueChange}
        placeholder={!isFilter ? placeholder : ''}
        disabled={state === 'disabled'}
        value={translate(value)}
        ref={inputRef}
        onBlur={onBlur}
      />
      {isFilter && (
        <div
          className={cn(
            'tw-absolute tw-inset-y-0 tw-left-0 tw-right-8 tw-flex tw-items-center tw-overflow-hidden',
            {
              'tw-pl-10': icon,
              'tw-pl-3': !icon,
            },
          )}
        >
          <Text
            as="span"
            className={cn(
              'tw-truncate tw-font-roboto tw-text-sm tw-leading-5 tw-text-zinc-900',
              {
                'tw-font-medium': !value,
                'tw-font-normal': value,
              },
            )}
            boldClassName="tw-font-medium"
          >
            {value ? translate(value) : placeholder}
          </Text>
        </div>
      )}
    </div>
  )

  return (
    <div className={boxClassName}>
      {label && (
        <Label className="tw-text-sm tw-font-medium tw-text-zinc-950">
          {label}
        </Label>
      )}
      <div className="tw-relative tw-my-1">
        <div
          className={cn(
            'tw-absolute tw-inset-y-0 tw-left-0 tw-z-10 tw-mr-2 tw-flex tw-items-center tw-pl-3 tw-text-gray-400',
            {
              'tw-text-orange-500': showOptions,
            },
          )}
        >
          {icon && <ComboBoxIcon icon={icon} />}
        </div>
        <Popover
          open={open ?? showOptions}
          onOpenChange={(isOpen) => {
            if (onOpenChange) {
              onOpenChange(isOpen)
              setIsFocused(!isFocused)
              return
            }
            setShowOptions(isOpen)
          }}
        >
          <PopoverTrigger
            asChild
            onMouseEnter={() => setIsMouseOnInput(true)}
            onMouseLeave={() => setIsMouseOnInput(false)}
          >
            <div>
              <Command className="tw-flex tw-justify-between">
                {tooltipContent ? (
                  <Tooltip
                    content={tooltipContent}
                    asChild
                    useDefaultCursor
                    className="tw-w-52"
                  >
                    {renderCommandInput()}
                  </Tooltip>
                ) : (
                  renderCommandInput()
                )}
                <button
                  type="button"
                  className="focus:tw-outline-hidden tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-cursor-pointer tw-items-center tw-rounded-r-md tw-border-none tw-bg-transparent tw-px-2 tw-shadow-sm tw-outline-none"
                  disabled={state === 'disabled'}
                  onClick={
                    isFilter && Boolean(value)
                      ? (event) => {
                          event.stopPropagation()
                          onClickClearFilter?.()
                          setIsFocused(false)
                        }
                      : handleFocus
                  }
                >
                  {isFilter && Boolean(value) ? (
                    <IconCircleX className="tw-size-5 tw-stroke-orange-500" />
                  ) : (
                    <IconChevronDown
                      className="tw-size-5 tw-text-gray-400"
                      aria-hidden="true"
                    />
                  )}
                </button>
              </Command>
            </div>
          </PopoverTrigger>
          <PopoverContent
            onOpenAutoFocus={(e) => e.preventDefault()}
            onPointerDownOutside={() =>
              !isMouseOnInput && setIsFocused(!isFocused)
            }
            className="tw-absolute tw-z-[99999] tw-w-[var(--radix-popover-trigger-width)] tw-p-0"
            align="start"
          >
            {children}
          </PopoverContent>
        </Popover>
      </div>
      {helper && (
        <Text
          className={cn('tw-m-0 tw-text-xs tw-normal-case', {
            'tw-text-rose-600': hasError,
            'tw-text-zinc-500': !hasError,
          })}
        >
          {helper}
        </Text>
      )}
    </div>
  )
}
